import { GA4Head } from './GA4';
import { GoogleAdsHead } from './GoogleAds';
import { GoogleConsentHead } from './GoogleConsent';
import { HubspotHead } from './Hubspot';
import type { ClientConfig } from '@archipro-website/config';
import { useMaybeAppData, useRootData } from '@modules/root';
import {
    hasSaleOrSharingOptOut,
    shouldAllowThirdPartySharing,
} from './allow-third-party-sharing';
import { isConfiguredPixelId } from './is-configured-pixel-id';

/**
 * Server-side rendered head pixels. Advertising pixels (Facebook, Pinterest,
 * LinkedIn, Outbrain) live in `ClientAdvertisingPixels`; the analytics trackers
 * here read the same shared third-party-sharing gate (GPC where the market
 * honours it, or an explicit sale/sharing opt-out from either the signed-in
 * member's preference or the opt-out cookie):
 *
 * - GA4 keeps loading, with Google Consent Mode defaulted to denied, so
 *   first-party aggregate measurement survives while ad/remarketing sharing
 *   stops.
 * - Google Ads is dropped entirely while the signal is active: unlike GA4 it
 *   has no measurement purpose beyond advertising.
 * - Hubspot is not gated: it operates as ArchiPro's service provider under its
 *   data-processing agreement, with no ad-audience features enabled, so a
 *   sale/sharing opt-out does not cover it. If Hubspot ad or audience-sync
 *   features are ever switched on, that flow must join this gate.
 */
export const HeadPixels = ({
    ga4TrackingId,
    googleAdsAccountIds,
    hubspotTrackingId,
    enableHubspot = true,
}: Pick<
    ClientConfig['pixels'],
    'ga4TrackingId' | 'googleAdsAccountIds' | 'hubspotTrackingId'
> & {
    /** When false, HubSpot tracking script is not injected (e.g. logged-in staff). */
    enableHubspot?: boolean;
}) => {
    const { localisation, globalPrivacyControl, saleSharingOptOut, user } =
        useRootData();
    const allowSharing = shouldAllowThirdPartySharing(
        localisation.countryConfig.honourGlobalPrivacyControl,
        globalPrivacyControl,
        hasSaleOrSharingOptOut(user) || saleSharingOptOut,
    );

    // `useMaybeAppData` (not `useAppData`) because this also renders inside
    // error boundaries, where root data may be absent.
    const appUser = useMaybeAppData()?.user;
    // user_id aggregates signed-in users in GA4; ap_user_id is the signed id
    // GrowthBook uses, negative for guests.
    const userID = appUser?.__typename === 'Me' ? appUser.ID : undefined;
    const apUserID = appUser?.ID
        ? appUser.ID * (appUser.__typename === 'Me' ? 1 : -1)
        : 0;

    return (
        <>
            <GoogleConsentHead denySharingConsent={!allowSharing} />
            {isConfiguredPixelId(ga4TrackingId) && (
                <GA4Head
                    id={ga4TrackingId}
                    userID={userID?.toString()}
                    apUserID={apUserID}
                />
            )}
            {allowSharing && (googleAdsAccountIds?.length ?? 0) > 0 && (
                <GoogleAdsHead accountIds={googleAdsAccountIds ?? []} />
            )}
            {enableHubspot && isConfiguredPixelId(hubspotTrackingId) && (
                <HubspotHead hubspotTrackingId={hubspotTrackingId} />
            )}
        </>
    );
};
